import React, { useState } from 'react';
import { Plus, X, BarChart3, Euro, MapPin, TrendingUp, TrendingDown, Minus, CheckCircle, AlertCircle } from 'lucide-react';

export default function PropertyComparisonTool() {
    const [properties, setProperties] = useState([]);
    const [addingProperty, setAddingProperty] = useState(false);
    const [url, setUrl] = useState('');

    const handleAddProperty = () => {
        if (url && properties.length < 4) {
            // Mock property data
            const mockProperty = {
                id: Date.now(),
                url,
                title: `Property ${properties.length + 1}`,
                location: 'Lisboa, Portugal',
                price: Math.floor(Math.random() * 500000) + 200000,
                score: Math.floor(Math.random() * 40) + 60,
                area: Math.floor(Math.random() * 100) + 50,
                pricePerM2: null,
                bedrooms: Math.floor(Math.random() * 3) + 2,
                bathrooms: Math.floor(Math.random() * 2) + 1,
                year: Math.floor(Math.random() * 30) + 1990,
                condition: ['Excellent', 'Good', 'Fair'][Math.floor(Math.random() * 3)],
                locationScore: Math.floor(Math.random() * 30) + 70,
                valueScore: Math.floor(Math.random() * 30) + 70,
                qualityScore: Math.floor(Math.random() * 30) + 70
            };
            mockProperty.pricePerM2 = Math.round(mockProperty.price / mockProperty.area);

            setProperties([...properties, mockProperty]);
            setUrl('');
            setAddingProperty(false);
        }
    };

    const handleRemoveProperty = (id) => {
        setProperties(properties.filter(p => p.id !== id));
    };

    const getBestValue = (metric) => {
        if (properties.length === 0) return null;

        const values = properties.map(p => p[metric]).filter(v => v != null);
        if (['score', 'locationScore', 'valueScore', 'qualityScore'].includes(metric)) {
            return Math.max(...values);
        }
        if (['price', 'pricePerM2'].includes(metric)) {
            return Math.min(...values);
        }
        return null;
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-600 bg-emerald-50';
        if (score >= 60) return 'text-yellow-600 bg-yellow-50';
        return 'text-gray-600 bg-gray-50';
    };

    const getComparisonIcon = (value, best, isInverse = false) => {
        if (value === best) return <CheckCircle className="w-5 h-5 text-emerald-600" />;
        if (isInverse) {
            return value > best ? <TrendingDown className="w-5 h-5 text-red-500" /> : <TrendingUp className="w-5 h-5 text-emerald-600" />;
        }
        return value > best ? <TrendingUp className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-red-500" />;
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                        <BarChart3 className="w-8 h-8 text-emerald-600" />
                        Property Comparison
                    </h1>
                    <p className="text-gray-600">Compare up to 4 properties side-by-side</p>
                </div>

                {/* Add Property Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    {!addingProperty ? (
                        <button
                            onClick={() => setAddingProperty(true)}
                            disabled={properties.length >= 4}
                            className="w-full px-6 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <Plus className="w-5 h-5" />
                            Add Property {properties.length > 0 && `(${properties.length}/4)`}
                        </button>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Property URL or Analysis ID
                                </label>
                                <input
                                    type="text"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://imovirtual.com/..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleAddProperty}
                                    className="flex-1 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                                >
                                    Add to Comparison
                                </button>
                                <button
                                    onClick={() => {
                                        setAddingProperty(false);
                                        setUrl('');
                                    }}
                                    className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Empty State */}
                {properties.length === 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Properties Added Yet</h3>
                        <p className="text-gray-600 mb-6">
                            Add your first property to start comparing analyses side-by-side
                        </p>
                    </div>
                )}

                {/* Comparison Table */}
                {properties.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 w-48">
                                            Metric
                                        </th>
                                        {properties.map((property) => (
                                            <th key={property.id} className="px-6 py-4 text-center relative">
                                                <button
                                                    onClick={() => handleRemoveProperty(property.id)}
                                                    className="absolute top-2 right-2 p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                                <div className="text-left">
                                                    <div className="font-semibold text-gray-900 mb-1">{property.title}</div>
                                                    <div className="text-xs text-gray-500 flex items-center gap-1">
                                                        <MapPin className="w-3 h-3" />
                                                        {property.location}
                                                    </div>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {/* Overall Score */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Overall Score</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-2xl font-bold px-3 py-1 rounded-lg ${getScoreColor(property.score)}`}>
                                                        {property.score}
                                                    </span>
                                                    {getComparisonIcon(property.score, getBestValue('score'))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Price */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Price</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg font-semibold text-gray-900">
                                                        €{property.price.toLocaleString()}
                                                    </span>
                                                    {getComparisonIcon(property.price, getBestValue('price'), true)}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Price per m² */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Price per m²</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg font-semibold text-gray-900">
                                                        €{property.pricePerM2}/m²
                                                    </span>
                                                    {getComparisonIcon(property.pricePerM2, getBestValue('pricePerM2'), true)}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Area */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Area</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4 text-gray-700">
                                                {property.area}m²
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Bedrooms */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Bedrooms</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4 text-gray-700">
                                                {property.bedrooms}
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Bathrooms */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Bathrooms</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4 text-gray-700">
                                                {property.bathrooms}
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Year Built */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Year Built</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4 text-gray-700">
                                                {property.year}
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Condition */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Condition</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${property.condition === 'Excellent' ? 'bg-emerald-100 text-emerald-800' :
                                                        property.condition === 'Good' ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {property.condition}
                                                </span>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Category Header */}
                                    <tr className="bg-gray-100">
                                        <td colSpan={properties.length + 1} className="px-6 py-3 text-sm font-bold text-gray-900">
                                            Detailed Scores
                                        </td>
                                    </tr>

                                    {/* Location Score */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Location Score</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-lg font-bold px-3 py-1 rounded-lg ${getScoreColor(property.locationScore)}`}>
                                                        {property.locationScore}
                                                    </span>
                                                    {getComparisonIcon(property.locationScore, getBestValue('locationScore'))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Value Score */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Value Score</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-lg font-bold px-3 py-1 rounded-lg ${getScoreColor(property.valueScore)}`}>
                                                        {property.valueScore}
                                                    </span>
                                                    {getComparisonIcon(property.valueScore, getBestValue('valueScore'))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Quality Score */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Quality Score</td>
                                        {properties.map((property) => (
                                            <td key={property.id} className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-lg font-bold px-3 py-1 rounded-lg ${getScoreColor(property.qualityScore)}`}>
                                                        {property.qualityScore}
                                                    </span>
                                                    {getComparisonIcon(property.qualityScore, getBestValue('qualityScore'))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
